import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { serviceService } from '../../services/serviceService';
import ConfirmModal from '../../components/Common/ConfirmModal';
import Toast from '../../components/Common/Toast';
import MutateGate from '../../components/Common/MutateGate';
import { useCanMutate } from '../../hooks/useCanMutate';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';
import {
  formatServiceClassificationLabel,
  isServicePendingClassification,
} from '../../utils/serviceClassification';

const Services = () => {
  const canMutate = useCanMutate();
  const { servicesClassificationEnabled } = useModuleSettings();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingServices, setLoadingServices] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedServiceType, setSelectedServiceType] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [classificationFilter, setClassificationFilter] = useState('all');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [serviceToDelete, setServiceToDelete] = useState(null);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const hasLoadedServices = useRef(false);

  useEffect(() => {
    if (!hasLoadedServices.current) {
      hasLoadedServices.current = true;
      loadServices();
    }
  }, []);

  const loadServices = async () => {
    setLoadingServices(true);
    console.log('📡 [Services] Cargando servicios desde backend...');
    
    try {
      const response = await serviceService.getServices();
      
      if (response.success && response.data) {
        console.log('✅ [Services] Servicios cargados exitosamente:', response.data.length);
        setServices(response.data);
      } else {
        console.warn('⚠️ [Services] Respuesta sin éxito:', response);
        setToast({
          isOpen: true,
          message: 'Error al cargar los servicios',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('❌ [Services] Error al cargar servicios:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar los servicios',
        type: 'error'
      });
    } finally {
      setLoadingServices(false);
    }
  };

  const handleDeleteClick = (service) => {
    setServiceToDelete(service);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!serviceToDelete) return;
    
    setLoading(true);
    console.log('🗑️ [Services] Eliminando servicio:', serviceToDelete.id);
    
    try {
      const response = await serviceService.deleteService(serviceToDelete.id);
      
      if (response.success) {
        console.log('✅ [Services] Servicio eliminado exitosamente');
        await loadServices();
        setIsDeleteModalOpen(false);
        setServiceToDelete(null);
        setToast({
          isOpen: true,
          message: 'Servicio eliminado exitosamente',
          type: 'success'
        });
      } else {
        throw new Error(response.message || 'Error al eliminar el servicio');
      }
    } catch (error) {
      console.error('❌ [Services] Error al eliminar servicio:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al eliminar el servicio',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Filtrar servicios (filtrado adicional en el frontend si es necesario)
  const filteredServices = services.filter(service => {
    const matchesSearch = !searchTerm || 
      (service.sku && service.sku.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (service.name && service.name.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesServiceType = !selectedServiceType || service.service_type === selectedServiceType;

    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && service.is_active) ||
      (statusFilter === 'inactive' && !service.is_active);

    const pending = isServicePendingClassification(service);
    const matchesClassification =
      !servicesClassificationEnabled ||
      classificationFilter === 'all' ||
      (classificationFilter === 'pending' && pending) ||
      (classificationFilter === 'classified' && !pending);

    return matchesSearch && matchesServiceType && matchesStatus && matchesClassification;
  });

  const pendingClassificationCount = servicesClassificationEnabled
    ? services.filter((s) => isServicePendingClassification(s)).length
    : 0;

  // Calcular estadísticas
  const totalServices = services.length;
  const activeServices = services.filter(s => s.is_active).length;
  const inactiveServices = services.filter(s => !s.is_active).length;
  const acquisitionServices = services.filter(s => s.service_type === 'acquisition').length;
  const saleServices = services.filter(s => s.service_type === 'sale').length;

  return (
    <div>
      <div className="p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Servicios</h1>
                <p className="text-gray-600 mt-1">Gestión de servicios de adquisición y venta</p>
              </div>
              <MutateGate>
                <Link
                  to="/services/create"
                  className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors inline-block"
                >
                  + Nuevo Servicio
                </Link>
              </MutateGate>
            </div>

            {/* Breadcrumb */}
            <nav className="text-sm text-gray-600 mb-4">
              <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
              <span className="mx-2">/</span>
              <span className="text-gray-800">Servicios</span>
            </nav>

            {/* Filtros y Búsqueda */}
            <div className="bg-white rounded-lg shadow-md p-4 mb-4">
              <div className={`grid grid-cols-1 gap-4 mb-4 ${servicesClassificationEnabled ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
                {/* Filtro por Tipo de Servicio */}
                <select
                  value={selectedServiceType}
                  onChange={(e) => setSelectedServiceType(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">Todos los Tipos</option>
                  <option value="acquisition">Adquisición de Servicios</option>
                  <option value="sale">Venta de Servicios</option>
                </select>

                {/* Filtro por Estado */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="all">Todos los Estados</option>
                  <option value="active">Activos</option>
                  <option value="inactive">Inactivos</option>
                </select>

                {servicesClassificationEnabled && (
                  <select
                    value={classificationFilter}
                    onChange={(e) => setClassificationFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="all">Toda clasificación</option>
                    <option value="pending">Pendientes de clasificación</option>
                    <option value="classified">Clasificados</option>
                  </select>
                )}

                {/* Búsqueda */}
                <input
                  type="text"
                  placeholder="Buscar por SKU o nombre..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
            </div>

            {/* Estadísticas */}
            <div className={`grid grid-cols-1 gap-4 mb-6 ${servicesClassificationEnabled ? 'md:grid-cols-6' : 'md:grid-cols-5'}`}>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Total</div>
                <div className="text-2xl font-bold text-gray-800">{totalServices}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Activos</div>
                <div className="text-2xl font-bold text-teal-600">{activeServices}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Inactivos</div>
                <div className="text-2xl font-bold text-gray-500">{inactiveServices}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Adquisición</div>
                <div className="text-2xl font-bold text-blue-600">{acquisitionServices}</div>
              </div>
              <div className="bg-white rounded-lg shadow-md p-4">
                <div className="text-sm text-gray-600">Venta</div>
                <div className="text-2xl font-bold text-purple-600">{saleServices}</div>
              </div>
              {servicesClassificationEnabled && (
                <div className="bg-white rounded-lg shadow-md p-4">
                  <div className="text-sm text-gray-600">Pend. clasificación</div>
                  <div className="text-2xl font-bold text-amber-600">{pendingClassificationCount}</div>
                </div>
              )}
            </div>
          </div>

          {/* Tabla de Servicios */}
          {loadingServices ? (
            <div className="text-center py-12">
              <div className="text-gray-500">Cargando servicios...</div>
            </div>
          ) : filteredServices.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <p className="text-gray-500">No se encontraron servicios</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      SKU
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nombre
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    {servicesClassificationEnabled && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Clasificación
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredServices.map((service) => {
                    const pendingClassification = servicesClassificationEnabled
                      && isServicePendingClassification(service);
                    const classificationLabel = formatServiceClassificationLabel(service);

                    return (
                    <tr
                      key={service.id}
                      className={`hover:bg-gray-50 ${pendingClassification ? 'bg-amber-50/60' : ''}`}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link
                          to={`/services/${service.id}/edit`}
                          className="text-sm font-mono text-teal-600 hover:text-teal-900"
                        >
                          {service.sku}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{service.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          service.service_type === 'acquisition' 
                            ? 'bg-blue-100 text-blue-800' 
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {service.service_type === 'acquisition' ? 'Adquisición' : 'Venta'}
                        </span>
                      </td>
                      {servicesClassificationEnabled && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          {pendingClassification ? (
                            <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                              Pendiente de clasificación
                            </span>
                          ) : (
                            <span className="text-sm text-gray-700" title={classificationLabel || ''}>
                              {classificationLabel || '—'}
                            </span>
                          )}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          service.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {service.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <Link
                          to={`/services/${service.id}/edit`}
                          className="text-purple-600 hover:text-purple-900 mr-4"
                          title={canMutate ? 'Editar' : 'Ver detalle'}
                        >
                          {canMutate ? 'Editar' : 'Ver'}
                        </Link>
                        <MutateGate>
                          <button
                            type="button"
                            onClick={() => handleDeleteClick(service)}
                            className="text-red-600 hover:text-red-900"
                            title="Eliminar"
                          >
                            Eliminar
                          </button>
                        </MutateGate>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmación de eliminación */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setServiceToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Eliminar Servicio"
        message={`¿Estás seguro de que deseas eliminar el servicio "${serviceToDelete?.name}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        isLoading={loading}
      />

      {/* Toast de notificaciones */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default Services;

